import { Tag } from 'primereact/tag';
import { Toast } from 'primereact/toast';
import React, { useEffect, useRef, useState } from 'react';
import { Card } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useShoppingCart } from '../../context/ShoppingCartContext';
import ImageModal from '../ImageModal/ImageModal';
import { AuthService } from '../../services/auth.service';
import './ProductCard.css';
import useAzureBlobImages from '../../hooks/useAzureBlobImages';
import { Skeleton } from 'primereact/skeleton';
import { formatCurrency } from '../../utils/formatCurrency';

interface ProductCardProps {
  nameProduct: string;
  code: string;
  price: number;
  available: boolean;
  isOnDemand: boolean;
  lowest: number;
  multiple: number;
  isEnabled: string;
}

const ProductCard: React.FC<ProductCardProps> = React.memo(
  ({
    nameProduct,
    code,
    price,
    available,
    isOnDemand,
    lowest,
    multiple,
    isEnabled,
  }) => {
    const { increaseCartQuantity } = useShoppingCart();
    const toast = useRef<Toast>(null);
    const [isImageModalOpen, setIsImageModalOpen] = useState(false);
    const { t } = useTranslation();

    const [imgSrc, setImgSrc] = useState(`/placeholder.jpg`);

    const { image, loading } = useAzureBlobImages(code);

    useEffect(() => {
      if (!loading && image && image.url) {
        setImgSrc(image.url);
      }
    }, [loading, image, code]);
    const save = () => {
      increaseCartQuantity(code, nameProduct, price, imgSrc, lowest, multiple);
      toast.current?.show({
        severity: 'success',
        summary: '¡Correcto!',
        detail: 'Elemento añadido correctamente.',
      });
    };

    const handleImageClick = () => {
      setIsImageModalOpen(true);
    };

    const handleCloseImageModal = () => {
      setIsImageModalOpen(false);
    };

    return (
      <>
        <Card
          style={{
            width: '13rem',
            height: '350px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            position: 'relative',
            zIndex: 1,
          }}
        >
          <div
            style={{
              maxHeight: '150px',
              overflow: 'hidden',
              display: 'flex',
              justifyContent: 'center',
              cursor: 'pointer',
            }}
            onClick={handleImageClick}
          >
            {loading ? (
              <Skeleton width="100%" height="150px" className="gray-skeleton" />
            ) : (
              <Card.Img
                src={imgSrc}
                alt={`Imagen del producto ${nameProduct}`}
                style={{ height: '100%', objectFit: 'cover' }}
              />
            )}
          </div>
          <Card.Body style={{ textAlign: 'center' }}>
            <Card.Title className="code-product">{code}</Card.Title>
            <Card.Title className="name-product">
              <strong>{nameProduct.toLocaleLowerCase()}</strong>
            </Card.Title>
            <Card.Text className="code-product">
              {price === 0 || price === null || price === undefined
                ? '-'
                : formatCurrency(price)}
            </Card.Text>

            <div className="tags-container">
              {available ? (
                <Tag
                  className="bg-green-100"
                  color="green"
                  icon="pi pi-check"
                  severity="warning"
                  value={t('card_component.tag.available')}
                />
              ) : (
                <Tag
                  className="bg-red-100"
                  icon="pi pi-times"
                  severity="warning"
                  value={t('card_component.tag.unavailable')}
                />
              )}

              {available && isOnDemand && (
                <Tag
                  className="bg-orange-100"
                  icon="pi pi-exclamation-circle"
                  severity="warning"
                  data-pr-tooltip={t('card_component.tag.on_demand')}
                  value={t('card_component.tag.on_demand')}
                />
              )}

              {available && lowest > 1 && (
                <Tag
                  className="bg-orange-100"
                  icon="pi pi-exclamation-circle"
                  severity="warning"
                  data-pr-tooltip={`${t('card_component.tag.sold_in_packs')} ${lowest}`}
                  value={`${t('card_component.tag.sold_in_packs')} ${lowest}`}
                />
              )}

              {available && isEnabled === 'Agotar' && (
                <Tag
                  className="bg-blue-100"
                  icon="pi pi-tag"
                  severity="warning"
                  value={t('card_component.tag.end_stock')}
                />
              )}
            </div>

            <button
              className="meso-button-card"
              onClick={save}
              disabled={
                AuthService.GetRole() === 'Marketing'
                  ? true
                  : false ||
                    !available ||
                    price === 0 ||
                    price === null ||
                    price === undefined
              }
            >
              {t('card_component.add_to_cart_btn')}
            </button>
          </Card.Body>
        </Card>

        {isImageModalOpen && (
          <ImageModal imgUrl={imgSrc} onClose={handleCloseImageModal} />
        )}
      </>
    );
  },
);

export default ProductCard;
